import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { ShieldCheck, Save, History, Loader2 } from "lucide-react";

/**
 * Business Protection configuration — company-wide settings that feed the
 * Business Protection Schedule contract variables.
 */
const BusinessProtection = () => {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [duration, setDuration] = useState("6 months");
  const [maxComp, setMaxComp] = useState("£5,000");
  const [clients, setClients] = useState("");
  const [depots, setDepots] = useState("");
  const [versions, setVersions] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/contracts/business-protection/settings");
      setEnabled(!!data.enabled);
      setDuration(data.duration || "6 months");
      setMaxComp(data.max_compensation || "£5,000");
      setClients((data.applicable_clients || []).join("\n"));
      setDepots((data.applicable_depots || []).join("\n"));
      setVersions(data.versions || []);
    } catch (e) { toast.error("Failed to load"); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        enabled,
        duration,
        max_compensation: maxComp,
        applicable_clients: clients.split("\n").map(s => s.trim()).filter(Boolean),
        applicable_depots:  depots.split("\n").map(s => s.trim()).filter(Boolean),
      };
      const { data } = await api.put("/contracts/business-protection/settings", payload);
      setVersions(data.versions || []);
      toast.success("Business protection settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    setBusy(false);
  };

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="p-6 space-y-6 max-w-3xl mx-auto">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-brand-red font-mono">Business Protection</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Business Protection Configuration</h1>
        <p className="text-sm text-slate-500 mt-1">
          These settings drive the Business Protection Schedule contract template. Changes are versioned.
        </p>
      </div>

      <Card className="p-6 rounded-2xl border-brand-border space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-brand-navy" />
            <span className="font-semibold text-sm">Enabled</span>
          </div>
          <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="bp-enabled" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Restriction duration</Label>
            <Input value={duration} onChange={(e) => setDuration(e.target.value)}
                    placeholder="e.g. 6 months" data-testid="bp-duration" />
          </div>
          <div>
            <Label className="text-xs">Maximum compensation cap</Label>
            <Input value={maxComp} onChange={(e) => setMaxComp(e.target.value)}
                    placeholder="e.g. £5,000" data-testid="bp-max" />
          </div>
        </div>

        <div>
          <Label className="text-xs">Applicable clients (one per line)</Label>
          <Textarea rows={4} value={clients} onChange={(e) => setClients(e.target.value)}
                     placeholder="DPD Group UK Limited" data-testid="bp-clients" />
        </div>
        <div>
          <Label className="text-xs">Applicable depots (one per line)</Label>
          <Textarea rows={4} value={depots} onChange={(e) => setDepots(e.target.value)}
                     placeholder="All depots the Driver has served" data-testid="bp-depots" />
        </div>

        <Button onClick={save} disabled={busy} className="bg-brand-red hover:bg-brand-red-hover"
                data-testid="bp-save">
          {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
          Save settings
        </Button>
      </Card>

      {versions.length > 0 && (
        <Card className="p-4 rounded-2xl border-brand-border">
          <div className="flex items-center gap-2 mb-3">
            <History className="w-4 h-4 text-brand-navy" />
            <div className="font-semibold text-sm">Version history ({versions.length})</div>
          </div>
          <div className="space-y-2 text-xs">
            {versions.slice().reverse().map((v, i) => (
              <div key={i} className="p-2 rounded bg-slate-50 border border-slate-200 flex justify-between gap-3">
                <div className="min-w-0">
                  <span className="font-mono text-[10px] text-slate-500">{v.saved_at?.slice(0, 19)}</span>
                  <div className="text-slate-700">
                    <strong>{v.duration}</strong> — cap {v.max_compensation}, {v.enabled ? "enabled" : "disabled"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

export default BusinessProtection;
